import React, { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const formatDate = (iso) => {
  if (!iso) return '—';
  const n = iso.toString().replace(' ', 'T').replace(/ \+00:00$/, 'Z').replace(/\+00:00$/, 'Z');
  const d = new Date(n);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
};

const parseBanDate = (iso) => {
  if (!iso) return null;
  const n = iso.toString().replace(' ', 'T').replace(/ \+00:00$/, 'Z').replace(/\+00:00$/, 'Z');
  const d = new Date(n);
  return isNaN(d.getTime()) ? null : d;
};

const BanCountdown = ({ bannedUntil }) => {
  const [remaining, setRemaining] = useState('');

  useEffect(() => {
    const calc = () => {
      const end = parseBanDate(bannedUntil);
      if (!end) return setRemaining('');
      const diff = end - Date.now();
      if (diff <= 0) return setRemaining('Expired');
      const days  = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const mins  = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      if (days > 0) setRemaining(`${days}d ${hours}h remaining`);
      else if (hours > 0) setRemaining(`${hours}h ${mins}m remaining`);
      else setRemaining(`${mins}m remaining`);
    };
    calc();
    const t = setInterval(calc, 60000);
    return () => clearInterval(t);
  }, [bannedUntil]);

  return (
    <span style={{
      background: 'rgba(255,159,71,0.12)', border: '1px solid rgba(255,159,71,0.35)',
      color: '#ff9f47', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700,
    }}>
      &#9888; {remaining}
    </span>
  );
};

const adminFetch = async (path, token, options = {}) => {
  const res = await fetch(`${API_URL}/admin${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Basic ${token}`,
      ...options.headers,
    },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
};

function AdminLogin({ onLogin }) {
  const [form, setForm]       = useState({ user: '', pass: '' });
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const token = btoa(`${form.user}:${form.pass}`);
    try {
      await adminFetch('/stats', token);
      onLogin(token);
    } catch {
      setError('Invalid username or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.loginWrap}>
      <div style={styles.loginCard}>
        <div style={styles.loginLogo}>TxtMyCar</div>
        <div style={styles.loginSub}>Admin Panel</div>
        {error && <div style={styles.alertError}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Username</label>
            <input style={styles.input} type="text" value={form.user}
              onChange={e => setForm(f => ({ ...f, user: e.target.value }))} required />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Password</label>
            <input style={styles.input} type="password" value={form.pass}
              onChange={e => setForm(f => ({ ...f, pass: e.target.value }))} required />
          </div>
          <button style={styles.btnPrimary} type="submit" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div style={{ ...styles.statCard, borderTopColor: accent || '#FFD93D' }}>
      <div style={{ ...styles.statValue, color: accent || '#FFD93D' }}>{value ?? '—'}</div>
      <div style={styles.statLabel}>{label}</div>
    </div>
  );
}

function BarChart({ data, color, label }) {
  if (!data || data.length === 0) return <div style={styles.empty}>No data yet</div>;
  const max = Math.max(...data.map(d => parseInt(d.count))) || 1;
  return (
    <div>
      <div style={styles.chartLabel}>{label}</div>
      <div style={styles.chartWrap}>
        {data.map((d, i) => (
          <div key={i} style={styles.chartBar}>
            <div style={{ flex: 1 - parseInt(d.count) / max }} />
            <div style={{ ...styles.chartFill, flexGrow: parseInt(d.count) / max, background: color }} />
            <div style={styles.chartCount}>{d.count}</div>
            <div style={styles.chartDate}>{d.date?.slice(5)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CreditEditor({ userId, currentCredits, token, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue]     = useState(currentCredits);
  const [saving, setSaving]   = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminFetch(`/users/${userId}/credits`, token, {
        method: 'PATCH',
        body: JSON.stringify({ credits: parseInt(value) }),
      });
      onUpdate(userId, parseInt(value));
      setEditing(false);
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ color: currentCredits > 0 ? '#FFD93D' : '#ff5252', fontWeight: 700 }}>
          {currentCredits}
        </span>
        <button onClick={() => { setValue(currentCredits); setEditing(true); }}
          style={{ background: 'rgba(255,217,61,0.1)', border: '1px solid rgba(255,217,61,0.3)', color: '#FFD93D', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: 11 }}>
          Edit
        </button>
      </span>
    );
  }

  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <input type="number" value={value} onChange={e => setValue(e.target.value)} min="0"
        style={{ width: 70, background: '#1e1e28', border: '1px solid #FFD93D', borderRadius: 4, color: '#f0f0f4', padding: '2px 6px', fontSize: 13 }} />
      <button onClick={handleSave} disabled={saving}
        style={{ background: '#FFD93D', border: 'none', color: '#0f0f13', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
        {saving ? '...' : 'Save'}
      </button>
      <button onClick={() => setEditing(false)}
        style={{ background: 'transparent', border: '1px solid #2a2a38', color: '#7a7a94', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: 11 }}>
        Cancel
      </button>
    </span>
  );
}

export default function Admin() {
  const [token, setToken]       = useState(() => sessionStorage.getItem('admin_token') || '');
  const [tab, setTab]           = useState('stats');
  const [traffic, setTraffic]    = useState(null);
  const [stats, setStats]       = useState(null);
  const [users, setUsers]       = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [search, setSearch]     = useState('');
  const [expandedUser, setExpandedUser] = useState(null);

  const handleLogin = (t) => { sessionStorage.setItem('admin_token', t); setToken(t); };
  const handleLogout = () => { sessionStorage.removeItem('admin_token'); setToken(''); };

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      if (tab === 'traffic') {
        setTraffic(await adminFetch('/traffic', token));
      } else if (tab === 'stats') {
        setStats(await adminFetch('/stats', token));
      } else if (tab === 'users') {
        setUsers(await adminFetch('/users', token));
      } else if (tab === 'vehicles') {
        setVehicles(await adminFetch('/vehicles', token));
      } else if (tab === 'messages') {
        setMessages(await adminFetch('/messages', token));
      }
    } catch (err) {
      setError(err.message);
      if (err.message === 'Invalid admin credentials') handleLogout();
    } finally {
      setLoading(false);
    }
  }, [token, tab]);

  useEffect(() => { load(); }, [load]);

  const deleteUser = async (id, email) => {
    if (!window.confirm(`Delete user ${email}? This will also delete their vehicles and messages.`)) return;
    try {
      await adminFetch(`/users/${id}`, token, { method: 'DELETE' });
      setUsers(u => u.filter(x => x.id !== id));
    } catch (err) { alert(err.message); }
  };

  const deleteVehicle = async (id, reg) => {
    if (!window.confirm(`Delete vehicle ${reg}?`)) return;
    try {
      await adminFetch(`/vehicles/${id}`, token, { method: 'DELETE' });
      setVehicles(v => v.filter(x => x.id !== id));
    } catch (err) { alert(err.message); }
  };

  const updateCredits = (userId, newCredits) => {
    setUsers(u => u.map(x => x.id === userId ? { ...x, credits: newCredits } : x));
  };

  const banUser = async (id) => {
    if (!window.confirm('Ban this user from sending messages for 7 days?')) return;
    try {
      const data = await adminFetch(`/users/${id}/ban`, token, { method: 'POST' });
      setUsers(u => u.map(x => x.id === id ? { ...x, banned_until: data.banned_until } : x));
    } catch (err) { alert(err.message); }
  };

  const unbanUser = async (id) => {
    try {
      await adminFetch(`/users/${id}/unban`, token, { method: 'POST' });
      setUsers(u => u.map(x => x.id === id ? { ...x, banned_until: null } : x));
    } catch (err) { alert(err.message); }
  };

  const isCurrentlyBanned = (bannedUntil) => {
    if (!bannedUntil) return false;
    const end = parseBanDate(bannedUntil);
    return end && end > new Date();
  };

  if (!token) return <AdminLogin onLogin={handleLogin} />;

  const filteredUsers    = users.filter(u =>
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.first_name?.toLowerCase().includes(search.toLowerCase())
  );
  const filteredVehicles = vehicles.filter(v =>
    v.registration?.toLowerCase().includes(search.toLowerCase()) ||
    v.User?.email?.toLowerCase().includes(search.toLowerCase())
  );
  const filteredMessages = messages.filter(m =>
    m.sender_registration?.toLowerCase().includes(search.toLowerCase()) ||
    m.recipient_registration?.toLowerCase().includes(search.toLowerCase())
  );

  const verifiedCount   = users.filter(u => u.email_verified == 1).length;
  const unverifiedCount = users.filter(u => u.email_verified != 1).length;

  return (
    <div style={styles.shell}>
      <aside style={styles.sidebar}>
        <div style={styles.sidebarLogo}>TxtMyCar</div>
        <div style={styles.sidebarSub}>Admin Panel</div>
        <nav style={styles.nav}>
          {['stats', 'traffic', 'users', 'vehicles', 'messages'].map(t => (
            <button key={t} style={{ ...styles.navItem, ...(tab === t ? styles.navActive : {}) }}
              onClick={() => { setTab(t); setSearch(''); }} capitaliz>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </nav>
        <button style={styles.logoutBtn} onClick={handleLogout}>Sign out</button>
      </aside>

      <main style={styles.main}>
        <div style={styles.mainHeader}>
          <h1 style={styles.pageTitle}>{tab.charAt(0).toUpperCase() + tab.slice(1)}</h1>
          <button style={styles.refreshBtn} onClick={load}>Refresh</button>
        </div>

        {error && <div style={styles.alertError}>{error}</div>}
        {loading && <div style={styles.loadingText}>Loading...</div>}

        {tab !== 'stats' && !loading && (
          <input style={styles.searchInput} placeholder={`Search ${tab}...`}
            value={search} onChange={e => setSearch(e.target.value)} />
        )}

                {tab === 'traffic' && !loading && traffic && (
          <div>
            <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
              <div style={styles.statCard}><div style={styles.statNum}>{traffic.totalHits.toLocaleString()}</div><div style={styles.statLabel}>Total page hits (30d)</div></div>
              <div style={styles.statCard}><div style={styles.statNum}>{traffic.days.reduce((a,d)=>a+d.unique,0).toLocaleString()}</div><div style={styles.statLabel}>Unique visitors (30d)</div></div>
              <div style={styles.statCard}><div style={styles.statNum}>{Math.round(traffic.totalHits/30)}</div><div style={styles.statLabel}>Avg hits / day</div></div>
            </div>
            <div style={styles.card}>
              <div style={styles.cardTitle}>Daily Traffic — Last 30 Days</div>
              <BarChart data={traffic.days.map(d=>({label:d.date,count:d.hits}))} color='#4f8ef7' label='Page hits' />
              <BarChart data={traffic.days.map(d=>({label:d.date,count:d.unique}))} color='#FFD93D' label='Unique visitors' />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
            <div style={styles.card}>
              <div style={styles.cardTitle}>Attack Summary</div>
              <div style={{display:'flex',gap:16,marginBottom:16,flexWrap:'wrap'}}>
                <div style={styles.statCard}><div style={{...styles.statNum,color:'#ff5252'}}>{traffic.totalAttacks}</div><div style={styles.statLabel}>Total attacks (30d)</div></div>
                <div style={styles.statCard}><div style={{...styles.statNum,color:'#ff9f47'}}>{traffic.topAttackIps.length}</div><div style={styles.statLabel}>Unique attacker IPs</div></div>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
                <div>
                  <div style={{fontSize:12,fontWeight:700,color:'#7a7a94',textTransform:'uppercase',letterSpacing:.5,marginBottom:8}}>Top attacking IPs</div>
                  <table style={styles.table}><thead><tr>
                    <th style={styles.th}>IP</th><th style={{...styles.th,textAlign:'right'}}>Attempts</th>
                  </tr></thead><tbody>
                  {traffic.topAttackIps.map((a,i)=>(
                    <tr key={i} style={styles.tr}>
                      <td style={{...styles.td,fontFamily:'monospace',fontSize:12,color:'#ff9f47'}}>{a.ip}</td>
                      <td style={{...styles.td,textAlign:'right',color:'#ff5252',fontWeight:700}}>{a.count}</td>
                    </tr>
                  ))}
                  </tbody></table>
                </div>
                <div>
                  <div style={{fontSize:12,fontWeight:700,color:'#7a7a94',textTransform:'uppercase',letterSpacing:.5,marginBottom:8}}>Recent attacks</div>
                  <table style={styles.table}><thead><tr>
                    <th style={styles.th}>IP</th>
                    <th style={styles.th}>Path</th>
                    <th style={{...styles.th,textAlign:'right'}}>Status</th>
                  </tr></thead><tbody>
                  {traffic.attacks.slice(0,15).map((a,i)=>(
                    <tr key={i} style={styles.tr}>
                      <td style={{...styles.td,fontFamily:'monospace',fontSize:11,color:'#ff9f47'}}>{a.ip}</td>
                      <td style={{...styles.td,fontFamily:'monospace',fontSize:11,maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{a.path}</td>
                      <td style={{...styles.td,textAlign:'right',fontSize:11,color:'#ff5252'}}>{a.status}</td>
                    </tr>
                  ))}
                  </tbody></table>
                </div>
              </div>
            </div>

              <div style={styles.card}>
                <div style={styles.cardTitle}>Top Pages</div>
                <table style={styles.table}><thead><tr>
                  <th style={styles.th}>Path</th><th style={{...styles.th,textAlign:'right'}}>Hits</th>
                </tr></thead><tbody>
                {traffic.topPages.map((p,i) => (
                  <tr key={i} style={styles.tr}>
                    <td style={{...styles.td,fontFamily:'monospace',fontSize:12}}>{p.path}</td>
                    <td style={{...styles.td,textAlign:'right',color:'#FFD93D',fontWeight:700}}>{p.count}</td>
                  </tr>
                ))}
                </tbody></table>
              </div>
              <div style={styles.card}>
                <div style={styles.cardTitle}>Top IPs</div>
                <table style={styles.table}><thead><tr>
                  <th style={styles.th}>IP Address</th><th style={{...styles.th,textAlign:'right'}}>Hits</th>
                </tr></thead><tbody>
                {traffic.topIps.map((p,i) => (
                  <tr key={i} style={styles.tr}>
                    <td style={{...styles.td,fontFamily:'monospace',fontSize:12}}>{p.ip}</td>
                    <td style={{...styles.td,textAlign:'right',color:'#FFD93D',fontWeight:700}}>{p.count}</td>
                  </tr>
                ))}
                </tbody></table>
              </div>
            </div>
          </div>
        )}

        {tab === 'stats' && !loading && stats && (
          <div>
            <div style={styles.statsGrid}>
              <StatCard label="Total Users"    value={stats.totals.totalUsers}    accent="#FFD93D" />
              <StatCard label="Total Vehicles" value={stats.totals.totalVehicles} accent="#47c8ff" />
              <StatCard label="Total Messages" value={stats.totals.totalMessages} accent="#ff7847" />
              <StatCard label="Revenue"        value={`£${((stats.totals.totalRevenue || 0) / 100).toFixed(2)}`} accent="#52ff8a" />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
              <div style={{ ...styles.card, display: 'flex', alignItems: 'center', gap: 20 }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 36, fontWeight: 800, color: '#52ff8a' }}>{verifiedCount}</div>
                  <div style={{ fontSize: 12, color: '#7a7a94' }}>Verified</div>
                </div>
                <div style={{ width: 1, background: '#2a2a38', alignSelf: 'stretch' }} />
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 36, fontWeight: 800, color: unverifiedCount > 0 ? '#ff5252' : '#7a7a94' }}>{unverifiedCount}</div>
                  <div style={{ fontSize: 12, color: '#7a7a94' }}>Unverified</div>
                </div>
                <div style={{ color: '#7a7a94', fontSize: 13, marginLeft: 8 }}>Email verification</div>
              </div>
              <div style={styles.chartCard}>
                <BarChart data={stats.recentMessages} color="#47c8ff" label="Messages — last 30 days" />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
              <div style={styles.chartCard}>
                <BarChart data={stats.recentUsers} color="#FFD93D" label="New users — last 30 days" />
              </div>
              <div style={styles.card}>
                <h2 style={styles.cardTitle}>Most active users</h2>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Email</th>
                      <th style={styles.th}>Messages</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.mostActiveUsers.map((u, i) => (
                      <tr key={i} style={styles.tr}>
                        <td style={styles.td}>{u.email}</td>
                        <td style={{ ...styles.td, color: '#FFD93D', fontWeight: 700 }}>{u.message_count}</td>
                      </tr>
                    ))}
                    {stats.mostActiveUsers.length === 0 && (
                      <tr><td colSpan={2} style={{ ...styles.td, color: '#7a7a94' }}>No messages yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {tab === 'users' && !loading && (
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>
              All users ({filteredUsers.length})
              <span style={{ marginLeft: 12, fontSize: 12, color: '#52ff8a', fontWeight: 400 }}>{verifiedCount} verified</span>
              <span style={{ marginLeft: 8, fontSize: 12, color: '#ff5252', fontWeight: 400 }}>{unverifiedCount} unverified</span>
            </h2>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Verified</th>
                  <th style={styles.th}>Credits</th>
                  <th style={styles.th}>Vehicles</th>
                  <th style={styles.th}>Joined</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(u => {
                  const banned = isCurrentlyBanned(u.banned_until);
                  const isExpanded = expandedUser === u.id;
                  return (
                    <React.Fragment key={u.id}>
                    <tr style={{ ...styles.tr, background: banned ? 'rgba(255,159,71,0.04)' : 'transparent', cursor: 'pointer' }}
                      onClick={() => setExpandedUser(isExpanded ? null : u.id)}>
                      <td style={styles.td}>{u.first_name} {u.last_name}</td>
                      <td style={styles.td}>{u.email}</td>
                      <td style={{ ...styles.td, textAlign: 'center' }}>
                        {u.email_verified == 1
                          ? <span style={{ color: '#52ff8a', fontSize: 18 }}>&#10003;</span>
                          : <span style={{ color: '#ff5252', fontSize: 18 }}>&#10007;</span>
                        }
                      </td>
                      <td style={styles.td}>
                        <CreditEditor userId={u.id} currentCredits={u.credits} token={token} onUpdate={updateCredits} />
                      </td>
                      <td style={styles.td}>{u.Vehicles?.map(v => v.registration).join(', ') || '—'}</td>
                      <td style={styles.td}>{formatDate(u.created_at)}</td>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                          {/* Ban status + countdown */}
                          {banned && <BanCountdown bannedUntil={u.banned_until} />}
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {banned ? (
                              <button style={styles.btnUnban} onClick={() => unbanUser(u.id)}>
                                Lift ban
                              </button>
                            ) : (
                              <button style={styles.btnBan} onClick={() => banUser(u.id)}>
                                Ban 7d
                              </button>
                            )}
                            <button style={styles.btnDelete} onClick={() => deleteUser(u.id, u.email)}>
                              Delete
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr style={{ background: '#1a1a24', borderBottom: '2px solid #FFD93D' }}>
                        <td colSpan={7} style={{ padding: '16px 20px' }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#7a7a94', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>Postal address for sticker</div>
                              <div style={{ fontSize: 14, color: '#f0f0f4', lineHeight: 2 }}>
                                <strong>{u.first_name} {u.last_name}</strong><br/>
                                {u.address1}<br/>
                                {u.address2 && <>{u.address2}<br/></>}
                                {u.city}{u.county ? ', ' + u.county : ''}<br/>
                                <strong style={{ letterSpacing: 2, color: '#FFD93D' }}>{u.postcode}</strong>
                              </div>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const addr = [u.first_name + ' ' + u.last_name, u.address1, u.address2, u.city, u.county, u.postcode].filter(Boolean).join('\n');
                                navigator.clipboard.writeText(addr);
                                alert('Address copied to clipboard!');
                              }}
                              style={{ background: 'rgba(255,217,61,0.1)', border: '1px solid rgba(255,217,61,0.3)', color: '#FFD93D', borderRadius: 8, padding: '10px 20px', cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'Syne, sans-serif', alignSelf: 'flex-start' }}>
                              Copy address
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
                {filteredUsers.length === 0 && (
                  <tr><td colSpan={7} style={{ ...styles.td, color: '#7a7a94' }}>No users found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'vehicles' && !loading && (
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>All vehicles ({filteredVehicles.length})</h2>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Registration</th>
                  <th style={styles.th}>Mobile</th>
                  <th style={styles.th}>Owner</th>
                  <th style={styles.th}>Registered</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredVehicles.map(v => (
                  <tr key={v.id} style={styles.tr}>
                    <td style={{ ...styles.td, fontFamily: 'monospace', fontWeight: 700, letterSpacing: 2, color: '#FFD93D' }}>{v.registration}</td>
                    <td style={styles.td}>{v.mobile_number}</td>
                    <td style={styles.td}>{v.User?.first_name} {v.User?.last_name} ({v.User?.email})</td>
                    <td style={styles.td}>{formatDate(v.created_at)}</td>
                    <td style={styles.td}>
                      <button style={styles.btnDelete} onClick={() => deleteVehicle(v.id, v.registration)}>Delete</button>
                    </td>
                  </tr>
                ))}
                {filteredVehicles.length === 0 && (
                  <tr><td colSpan={5} style={{ ...styles.td, color: '#7a7a94' }}>No vehicles found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'messages' && !loading && (
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>Recent messages ({filteredMessages.length})</h2>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>From</th>
                  <th style={styles.th}>To</th>
                  <th style={styles.th}>Message</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Sent</th>
                </tr>
              </thead>
              <tbody>
                {filteredMessages.map(m => (
                  <tr key={m.id} style={styles.tr}>
                    <td style={{ ...styles.td, fontFamily: 'monospace', fontWeight: 700, color: '#FFD93D' }}>{m.sender_registration}</td>
                    <td style={{ ...styles.td, fontFamily: 'monospace', fontWeight: 700, color: '#47c8ff' }}>{m.recipient_registration}</td>
                    <td style={{ ...styles.td, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.message_body}</td>
                    <td style={{ ...styles.td, color: (m.status === 'delivered' || m.status === 'sent' || m.status === 'queued') ? '#52ff8a' : '#ff5252' }}>
                      {m.status === 'queued' || m.status === 'sent' ? 'Sent' : m.status === 'delivered' ? 'Delivered' : m.status}
                    </td>
                    <td style={styles.td}>{formatDate(m.created_at)}</td>
                  </tr>
                ))}
                {filteredMessages.length === 0 && (
                  <tr><td colSpan={5} style={{ ...styles.td, color: '#7a7a94' }}>No messages found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}

const styles = {
  shell:       { display: 'flex', minHeight: '100vh', background: '#0f0f13', color: '#f0f0f4', fontFamily: "'DM Sans', sans-serif" },
  sidebar:     { width: 200, background: '#17171e', borderRight: '1px solid #2a2a38', display: 'flex', flexDirection: 'column', padding: '24px 0', flexShrink: 0 },
  sidebarLogo: { fontFamily: 'Syne, sans-serif', fontSize: 18, fontWeight: 800, color: '#FFD93D', padding: '0 20px', marginBottom: 4 },
  sidebarSub:  { fontSize: 11, color: '#7a7a94', padding: '0 20px', marginBottom: 24, textTransform: 'uppercase', letterSpacing: 1 },
  nav:         { display: 'flex', flexDirection: 'column', gap: 2, padding: '0 8px', flex: 1 },
  navItem:     { background: 'transparent', border: 'none', color: '#7a7a94', padding: '10px 12px', borderRadius: 8, cursor: 'pointer', textAlign: 'left', fontFamily: 'Syne, sans-serif', fontWeight: 600, fontSize: 14 },
  navActive:   { background: 'rgba(255,217,61,0.1)', color: '#FFD93D' },
  logoutBtn:   { margin: '16px 8px 0', background: 'transparent', border: '1px solid #2a2a38', color: '#7a7a94', padding: '8px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontFamily: "'DM Sans', sans-serif" },
  main:        { flex: 1, padding: 32, overflowX: 'auto' },
  mainHeader:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 },
  pageTitle:   { fontFamily: 'Syne, sans-serif', fontSize: 28, fontWeight: 800, letterSpacing: -1 },
  refreshBtn:  { background: 'rgba(255,217,61,0.1)', border: '1px solid rgba(255,217,61,0.3)', color: '#FFD93D', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontFamily: 'Syne, sans-serif', fontWeight: 600, fontSize: 13 },
  statsGrid:   { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 },
  statCard:    { background: '#17171e', border: '1px solid #2a2a38', borderRadius: 16, padding: 24, borderTop: '2px solid #FFD93D' },
  statValue:   { fontFamily: 'Syne, sans-serif', fontSize: 48, fontWeight: 800, letterSpacing: -2, lineHeight: 1, marginBottom: 6 },
  statLabel:   { color: '#7a7a94', fontSize: 13 },
  chartCard:   { background: '#17171e', border: '1px solid #2a2a38', borderRadius: 16, padding: 24 },
  chartLabel:  { fontSize: 13, fontWeight: 600, color: '#7a7a94', marginBottom: 16, textTransform: 'uppercase', letterSpacing: 0.5 },
  chartWrap:   { display: 'flex', alignItems: 'flex-end', gap: 4, height: 100, overflowX: 'auto' },
  chartBar:    { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, flex: '0 0 auto', minWidth: 24, height: '100%' },
  chartFill:   { width: 16, borderRadius: 3, minHeight: 2 },
  chartCount:  { fontSize: 9, color: '#7a7a94' },
  chartDate:   { fontSize: 8, color: '#4a4a5a', transform: 'rotate(-45deg)', whiteSpace: 'nowrap' },
  card:        { background: '#17171e', border: '1px solid #2a2a38', borderRadius: 16, padding: 24, marginBottom: 24 },
  cardTitle:   { fontFamily: 'Syne, sans-serif', fontSize: 16, fontWeight: 700, marginBottom: 16 },
  table:       { width: '100%', borderCollapse: 'collapse' },
  th:          { textAlign: 'left', padding: '8px 12px', fontSize: 11, fontWeight: 700, color: '#7a7a94', textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid #2a2a38' },
  tr:          { borderBottom: '1px solid #1e1e28' },
  td:          { padding: '10px 12px', fontSize: 13, color: '#f0f0f4' },
  btnDelete:   { background: 'rgba(255,82,82,0.1)', border: '1px solid rgba(255,82,82,0.3)', color: '#ff5252', padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12 },
  btnBan:      { background: 'rgba(255,159,71,0.1)', border: '1px solid rgba(255,159,71,0.3)', color: '#ff9f47', padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12 },
  btnUnban:    { background: 'rgba(82,255,138,0.1)', border: '1px solid rgba(82,255,138,0.3)', color: '#52ff8a', padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12 },
  searchInput: { background: '#1e1e28', border: '1px solid #2a2a38', borderRadius: 10, color: '#f0f0f4', fontSize: 14, padding: '10px 16px', width: '100%', maxWidth: 400, marginBottom: 16, outline: 'none', fontFamily: "'DM Sans', sans-serif" },
  alertError:  { background: 'rgba(255,82,82,0.1)', border: '1px solid rgba(255,82,82,0.3)', color: '#ff5252', borderRadius: 10, padding: '10px 16px', marginBottom: 16, fontSize: 14 },
  loadingText: { color: '#7a7a94', fontSize: 14, marginBottom: 16 },
  empty:       { color: '#7a7a94', fontSize: 13, padding: '20px 0' },
  loginWrap:   { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f0f13' },
  loginCard:   { background: '#17171e', border: '1px solid #2a2a38', borderRadius: 20, padding: 48, width: '100%', maxWidth: 380 },
  loginLogo:   { fontFamily: 'Syne, sans-serif', fontSize: 24, fontWeight: 800, color: '#FFD93D', marginBottom: 4 },
  loginSub:    { color: '#7a7a94', fontSize: 13, marginBottom: 32, textTransform: 'uppercase', letterSpacing: 1 },
  formGroup:   { marginBottom: 16 },
  label:       { display: 'block', fontSize: 11, fontWeight: 600, color: '#7a7a94', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 },
  input:       { background: '#1e1e28', border: '1px solid #2a2a38', borderRadius: 10, color: '#f0f0f4', fontFamily: "'DM Sans', sans-serif", fontSize: 15, padding: '12px 16px', width: '100%', outline: 'none', boxSizing: 'border-box' },
  btnPrimary:  { background: '#FFD93D', color: '#0f0f13', border: 'none', borderRadius: 10, padding: '14px', width: '100%', fontFamily: 'Syne, sans-serif', fontSize: 15, fontWeight: 700, cursor: 'pointer', marginTop: 8 },
};
